/**
 * Generic comma/enter-separated string list picker (chips + input).
 * Used by ISP, brand, city, region, referrer, IP ranges, etc.
 */

import { useState, type FC, type KeyboardEvent } from 'react';
import { X } from 'lucide-react';

import { Input } from '@/components/ui/form/input/Input';

export interface StringListPickerProps {
  values: readonly string[];
  onChange: (next: readonly string[]) => void;
  placeholder?: string;
  ariaLabel: string;
}

const StringListPicker: FC<StringListPickerProps> = ({
  values,
  onChange,
  placeholder = 'Type a value, press Enter',
  ariaLabel,
}) => {
  const [draft, setDraft] = useState('');

  const commit = () => {
    const trimmed = draft.trim();
    if (!trimmed) return;
    if (values.includes(trimmed)) {
      setDraft('');
      return;
    }
    onChange([...values, trimmed]);
    setDraft('');
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      commit();
      return;
    }
    if (e.key === 'Backspace' && draft.length === 0 && values.length > 0) {
      onChange(values.slice(0, -1));
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-1.5 rounded-md border border-border/60 bg-background px-1.5 py-1">
      {values.map((v) => (
        <span
          key={v}
          className="inline-flex items-center gap-1 rounded-full border border-border bg-muted/40 px-2 py-0.5 text-[11px] text-foreground"
        >
          <span className="max-w-[10rem] truncate">{v}</span>
          <button
            type="button"
            aria-label={`Remove ${v}`}
            className="text-muted-foreground hover:text-destructive"
            onClick={() => onChange(values.filter((x) => x !== v))}
          >
            <X className="h-3 w-3" aria-hidden />
          </button>
        </span>
      ))}
      <Input
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={onKeyDown}
        placeholder={values.length === 0 ? placeholder : ''}
        aria-label={ariaLabel}
        className="h-7 min-w-[8rem] flex-1 border-0 bg-transparent text-xs shadow-none focus-visible:ring-0"
      />
    </div>
  );
};

export default StringListPicker;
